// The app's own browser-side data, for Settings > Database: every localStorage
// entry (UI preferences, cookie consent, anything named like a key), with
// secret-looking values masked until the user reveals them.
export interface LocalEntry {
  key: string;
  value: string;
  bytes: number;
  sensitive: boolean;
}

const SENSITIVE = /(secret|token|password|passwd|session|csrf|auth|(^|[._-])keys?($|[._-])|apikey|private)/i;
export const isSensitiveKey = (key: string): boolean => SENSITIVE.test(key);

export const maskValue = (v: string): string => (v.length <= 4 ? '••••' : `${'•'.repeat(Math.min(12, v.length - 2))}${v.slice(-2)}`);

export function readLocalEntries(storage: Pick<Storage, 'length' | 'key' | 'getItem'> | undefined = safeStorage()): LocalEntry[] {
  if (!storage) return [];
  const out: LocalEntry[] = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key === null) continue;
      const value = storage.getItem(key) ?? '';
      out.push({ key, value, bytes: (key.length + value.length) * 2, sensitive: isSensitiveKey(key) });
    }
  } catch {
    /* storage blocked */
  }
  return out.sort((a, b) => a.key.localeCompare(b.key));
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export const formatBytes = (n: number): string => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`);
